import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import {BulkChangePriceAttemptedProductsDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import {booleanComparator, createUUId} from "../../../../../../../../../core/services/utils/utils";
import {DefaultInitialPaginationInfo, DefaultPaginationInfo} from "../../../../../../../../../core/constants/enums";
import {ReactComponent as Passed} from "../../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../../assets/images/validations/failed.svg";
import {ReactComponent as Warning} from "../../../../../../../../../assets/images/validations/warning.svg";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import SearchInput from "../../../../../../../../components/base/input/search_input";
import useCachedPriceLevelsDictionaryController from "../../../../../../../../hooks/use-price-level-dictionary";
import ProductInformationPopover from "../../../../../../../../components/app-specific/draggable-popover/product-information-popover";


const BulkPriceChangeReportAttemptedProductsTable = ({
                                                         requestId,
                                                         attemptId,
                                                         newLogs: newLogsProps,
                                                         showAttempt,
                                                         saveConfig,
                                                     }) => {
    const [logs, setLogs] = useState([]);
    const [newLogs, setNewLogs] = useState(newLogsProps?.map(e => ({...e, new: true})) ?? []);
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(true);
    const isMounted = useIsMounted();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.new,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 10,
                },
                {
                    title: "Attempt",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.attempt,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 80,
                },
                {
                    title: "Status",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.status,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 50,
                },
                {
                    title: "Part Number",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.partNumber,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Category",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.category,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Price Level",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.priceLevel,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Old Price",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.oldPrice,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                    sortable: false,
                },
                {
                    title: "New Price",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.newPrice,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                    sortable: false,
                },
                {
                    title: "Error Description",
                    name: BulkChangePriceAttemptedProductsDataGridCellKeys.errorDescription,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 250,
                },
            ].filter(e => !showAttempt ? e.name !== BulkChangePriceAttemptedProductsDataGridCellKeys.attempt : true),
        [showAttempt])

    const [dataGridColumns, _, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: saveConfig?.name,
        version: saveConfig?.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize]);
    const defaultInitialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultInitialPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultInitialPaginationInfo, [dataGridPageSize])

    const [paginationInfo, setPaginationInfo] = useState(defaultInitialPaginationInfo);

    const dataGridData = useMemo(() => {
        const _logs = Array.from(logs ?? []);
        _logs.unshift(...newLogs);
        for (const log of _logs) {
            log.new = log.new ?? false;
        }
        _logs.sort((a, b) => booleanComparator(b.new, a.new))
        return _logs.slice(0, paginationInfo.pageSize);
    }, [logs, newLogs, paginationInfo.pageSize])

    /**
     * With each change in the [newLogsProps] list value:
     * - sets the [newLogs] state value such that:
     *  1. the newLogs and newLogProps are merged together
     *  2. logs that are not in newLogProps are old
     *  3. logs that re in the newLogProps are new
     */
    useLayoutEffect(() => {
        setNewLogs(prevState => {
            const _newLogsProps = Array.from(newLogsProps ?? []);
            const mustBeAdded = [];
            for (const log of _newLogsProps) {
                const found = prevState.find(e => (
                    log.partId === e.partId &&
                    log.priceLevelId === e.priceLevelId &&
                    log.categoryId === e.categoryId &&
                    log.try?.id === e.try?.id &&
                    log.isSuccess === e.isSuccess
                ));
                if (!found) {
                    mustBeAdded.push({...log, new: true})
                }
            }
            const merged = [...mustBeAdded, ...prevState];
            for (const log of merged) {
                const found = _newLogsProps.find(e => (
                    log.partId === e.partId &&
                    log.priceLevelId === e.priceLevelId &&
                    log.categoryId === e.categoryId &&
                    log.try?.id === e.try?.id &&
                    log.isSuccess === e.isSuccess
                ));
                if (!found)
                    log.new = false;
            }
            return merged;
        })
    }, [newLogsProps])

    /**
     * With each change in the [keyword]:
     * - resets the [pagination] state value to trigger the search function.
     */
    useEffect(() => {
        if (keyword === null)
            return setKeyword('');
        setPaginationInfo(defaultInitialPaginationInfo);
        setLogs([]);
    }, [keyword, requestId, attemptId])

    /**
     * With each change in the [pagination] state value:
     * - searches for the attempted products of the current bulk-price-change request.
     */
    useEffect(() => {
        if (paginationInfo.currentPage === defaultInitialPaginationInfo.currentPage) {
            return setPaginationInfo(initialPaginationInfo)
        }
        searchAttemptedProducts().then();
    }, [paginationInfo.currentPage, paginationInfo.pageSize])

    /**
     * Searches among the affected products of the current bulk-price-change request with the given constraints:
     * @return {Promise<void>}
     */
    const searchAttemptedProducts = async () => {
        setLoading(true);
        const forApi = prepareSearchData();
        const [response, priceLevelDict] = await Promise.all([
            Api.getBulkPriceChangePriceChangeLogs(forApi),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setNewLogs([]);
            setLogs(response?.data?.items
                    ?.map(e => ({
                        ...e,
                        priceLevelTitle: priceLevelDict?.find(p => p.priceLevelId === e.priceLevelId)?.title ?? e.priceLevelTitle,
                    }))
                ?? []);
            setPaginationInfo(prevState => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }))
        }
    }

    /**
     * Prepares the search request body.
     */
    const prepareSearchData = () => {
        return {
            requestId: requestId,
            tryId: attemptId,
            keyword: !keyword?.trim()?.length ? null : keyword.trim(),
            pagination: paginationInfo,
        };
    }

    /**
     * Memo version of table row records used in data grid
     */
    const dataGridRows = useMemo(() => {
        return dataGridData?.map((log) => ({
            key: `${log.partId}-${log.categoryId}-${log.priceLevelId ?? createUUId(true)}`,
            data: log,
            cells: {
                [BulkChangePriceAttemptedProductsDataGridCellKeys.new]:
                    log.new
                        ? <div className={'new-indicator'}>NEW</div>
                        : <></>,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.attempt]: `Attempt ${log.try?.index ?? 0}`,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.partNumber]:
                    <ProductInformationPopover partNo={log.partNumber}>
                        {log.partNumber ?? '--'}
                    </ProductInformationPopover>,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.category]: log.categoryName ?? log.categoryTitle,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.priceLevel]: log.priceLevelTitle,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.oldPrice]: log.oldPrice,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.newPrice]: log.newPrice,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.status]:
                    log.isCanceled
                        ? <Warning/>
                        : log.isSuccess
                            ? <Passed/>
                            : <Failed/>,
                [BulkChangePriceAttemptedProductsDataGridCellKeys.errorDescription]: log.errorDescription ?? log.description,
            }
        }))
    }, [dataGridData])

    return (
        <>
            <div>
                <SearchInput
                    autoSearch
                    loading={loading}
                    initialValue={keyword ?? ""}
                    search={setKeyword}
                    label={'Search'}
                />
                <div className={'mb-2'}/>
                <div className={'content'}>
                    <PartnerDataGrid
                        hideFooterActions
                        hideExporting
                        {...(saveConfig ?? {})}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        state={{
                            pagination: paginationInfo,
                            loading: {state: loading},
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                        onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                    />
                </div>
            </div>
        </>
    );
}

export default BulkPriceChangeReportAttemptedProductsTable;
